import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X, Globe, LogIn, LogOut, User } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const asset = (path) => `${process.env.PUBLIC_URL || ''}${path}`;

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [language, setLanguage] = useState('EN');
  const location = useLocation();
  const { user, authLoading, authBusy, loginWithGoogle, logout } = useAuth();

  useEffect(() => {
    const applyLanguage = (value) => {
      setLanguage(value || localStorage.getItem('language') || 'EN');
    };

    applyLanguage(localStorage.getItem('language'));

    const onLanguageChange = (e) => applyLanguage(e.detail);
    const onStorage = (e) => {
      if (e.key === 'language') applyLanguage(e.newValue);
    };

    window.addEventListener('languageChange', onLanguageChange);
    window.addEventListener('storage', onStorage);

    return () => {
      window.removeEventListener('languageChange', onLanguageChange);
      window.removeEventListener('storage', onStorage);
    };
  }, []);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 18);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setIsOpen(false);
  }, [location.pathname, user]);

  const navItems = [
    { name: 'Home', path: '/', tr: 'Ana Sayfa' },
    { name: 'Our Games', path: '/games', tr: 'Oyunlarımız' },
    { name: 'Play', path: '/play', tr: 'Oyna' },
    { name: 'News', path: '/news', tr: 'Haberler' },
    { name: "What's Coming", path: '/coming', tr: 'Yakında' },
    { name: 'About Us', path: '/about', tr: 'Hakkımızda' },
    { name: 'Contact', path: '/contact', tr: 'İletişim' }
  ];

  const toggleLanguage = () => {
    const next = language === 'EN' ? 'TR' : 'EN';
    setLanguage(next);
    localStorage.setItem('language', next);
    window.dispatchEvent(new CustomEvent('languageChange', { detail: next }));
  };

  const authTexts = {
    login: language === 'EN' ? 'Login' : 'Giriş Yap',
    logout: language === 'EN' ? 'Logout' : 'Çıkış Yap',
    loading: language === 'EN' ? 'Loading...' : 'Yükleniyor...',
    account: language === 'EN' ? 'Account' : 'Hesap'
  };

  const userName =
    user?.displayName?.trim()?.split(' ')?.[0] ||
    user?.email?.split('@')?.[0] ||
    authTexts.account;

  const navSurface = scrolled || isOpen
    ? 'bg-[#0b0a0f]/95 shadow-[0_12px_30px_rgba(0,0,0,0.18)]'
    : 'bg-gradient-to-b from-black/55 to-transparent';

  return (
    <nav className={`fixed inset-x-0 top-0 z-50 transition-colors duration-200 ${navSurface}`}>
      <div className="mx-auto flex h-20 w-full max-w-[1800px] items-center justify-between gap-4 px-5 sm:px-8 lg:px-10 xl:px-14 2xl:px-16">
        <Link to="/" className="flex min-w-0 items-center gap-2.5">
          <img
            src={asset('/assets/images/eblogo.png')}
            alt="E&B Games official logo"
            className="h-9 w-auto shrink-0 rounded-md object-contain select-none"
            draggable={false}
          />
          <span className="hidden sm:block truncate font-gaming text-lg tracking-wide text-white">
            E&B Games
          </span>
        </Link>

        <div className="hidden xl:flex items-center gap-2 2xl:gap-3">
          {navItems.map((item) => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`relative rounded-lg px-3.5 py-2.5 font-display text-[17px] font-semibold transition-colors duration-150 2xl:px-4 2xl:text-[18px] ${
                  active
                    ? 'text-white'
                    : 'text-gray-300 hover:bg-white/[0.05] hover:text-white'
                }`}
              >
                {language === 'EN' ? item.name : item.tr}
                {active && (
                  <span className="absolute inset-x-3.5 -bottom-[1px] h-[2px] rounded-full bg-gaming-primary 2xl:inset-x-4" />
                )}
              </Link>
            );
          })}
        </div>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLanguage}
            className="flex h-11 min-w-[112px] items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.045] px-3 text-base text-gray-200 transition-colors hover:bg-white/[0.075] hover:text-white"
            aria-label="Change language"
          >
            <Globe size={17} />
            <span className="font-display font-semibold">{language}</span>
          </button>

          <div className="hidden xl:flex items-center gap-2">
            {user ? (
              <>
                <div className="flex h-11 min-w-[112px] max-w-[150px] items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.045] px-3 text-white">
                  {user.photoURL ? (
                    <img
                      src={user.photoURL}
                      alt={userName}
                      className="h-5 w-5 shrink-0 rounded-full"
                      referrerPolicy="no-referrer"
                    />
                  ) : (
                    <User size={16} className="shrink-0 text-gaming-primary" />
                  )}
                  <span className="truncate font-display text-sm">{userName}</span>
                </div>

                <button
                  onClick={logout}
                  disabled={authBusy}
                  className="flex h-11 min-w-[112px] items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.045] px-3 font-display text-[15px] text-gray-300 transition-colors hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
                >
                  <LogOut size={16} />
                  <span>{authTexts.logout}</span>
                </button>
              </>
            ) : (
              <button
                onClick={loginWithGoogle}
                disabled={authBusy || authLoading}
                className="flex h-11 min-w-[112px] items-center justify-center gap-2 rounded-lg border border-white/10 bg-white/[0.045] px-3 font-display text-[15px] text-gray-200 transition-colors hover:bg-white/[0.075] hover:text-white disabled:cursor-not-allowed disabled:opacity-50"
              >
                <LogIn size={16} />
                <span>{authLoading ? '...' : authTexts.login}</span>
              </button>
            )}
          </div>

          <button
            onClick={() => setIsOpen((value) => !value)}
            className="rounded-lg border border-white/10 bg-white/[0.045] p-2.5 text-white xl:hidden"
            aria-label="Toggle navigation"
            aria-expanded={isOpen}
          >
            {isOpen ? <X size={23} /> : <Menu size={23} />}
          </button>
        </div>
      </div>

      {isOpen && (
        <div className="border-t border-white/10 bg-[#0b0a0f] px-4 pb-5 pt-3 xl:hidden">
          <div className="mx-auto max-w-[1800px]">
            <div className="mb-3 border-b border-white/10 pb-3">
              {user ? (
                <div className="space-y-2">
                  <div className="flex items-center gap-3 rounded-xl bg-white/[0.04] px-3 py-3">
                    {user.photoURL ? (
                      <img
                        src={user.photoURL}
                        alt={userName}
                        className="h-9 w-9 rounded-full"
                        referrerPolicy="no-referrer"
                      />
                    ) : (
                      <User size={18} className="text-gaming-primary" />
                    )}
                    <div className="min-w-0 font-display">
                      <div className="truncate text-white">{userName}</div>
                      <div className="truncate text-xs text-gray-500">{user?.email}</div>
                    </div>
                  </div>
                  <button
                    onClick={logout}
                    disabled={authBusy}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-3 font-display text-gray-300"
                  >
                    <LogOut size={17} />
                    {authTexts.logout}
                  </button>
                </div>
              ) : (
                <button
                  onClick={loginWithGoogle}
                  disabled={authBusy || authLoading}
                  className="flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 px-4 py-3 font-display text-gray-200"
                >
                  <LogIn size={17} />
                  {authLoading
                    ? authTexts.loading
                    : language === 'EN'
                    ? 'Login with Google'
                    : 'Google ile Giriş Yap'}
                </button>
              )}
            </div>

            <div className="grid gap-1">
              {navItems.map((item) => {
                const active = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    className={`rounded-xl px-4 py-3 font-display text-lg transition-colors ${
                      active ? 'bg-white/[0.07] text-white' : 'text-gray-300 hover:bg-white/[0.04]'
                    }`}
                  >
                    {language === 'EN' ? item.name : item.tr}
                  </Link>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
